import React, { useEffect, useState } from "react";
import { Card, Container, Grid, Typography } from "@mui/material";

import DashboardLayout from "../../layouts/DashboardLayout";
import DashboardHeader from "./DashboardHeader";
import TotalEmissionChart from "./TotalEmissionChart";
import EmissionChart from "./EmissionChart";
import GeographicalChart from "./GeographicalChart";
import Statistics from "./Statistics";
import useStyles from "./styles";
import FuelSourceGraph from "./FuelSourceGraph";
import AuditTrends from "../AuditorDashboard/AuditTrends";
import { getCookie } from "../../services/cookie";
import RoleDistributionGraph from "./RoleDistributionGraph";
import InitiativeTrackingGraph from "./InitiativeTrackingGraph";
import EnergyMaterialConsumption from "./EnergyMaterialConsumption";
import FuelSourceTargetGraph from "./FuelSourcesSankeyGraph";
import EmissionsCard from "./EmissionsCard";
import DiversityGraph from "./DiversityGraph";
import DevelopmentTrainingGraph from "./DevelopmentTrainingGraph";
import SocialEngagementGraph from "./SocialEngagementGraph";
import EmployeeHireResignGraph from "./EmployeeHireResignGraph";
import BoardDiversityGraph from "./BoardDiversityGraph";
import ManagementDiversityGraph from "./ManagementDiversityGraph";
import EmployeeTurnoverGraph from "./EmployeeTurnoverGraph";
import EconomicGraph from "./EconomicGraph";
import EthicalBehaviourGraph from "./EthicalBehaviourGraph";
import AntiCorruptionDisclosureGraph from "./AntiCorruptionDisclosureGraph";
import AntiCorruptionTrainingGraph from "./AntiCorruptionTrainingGraph";
import MagicQuadrant from "./MagicQuadrant";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import {
  getNotifications,
  stakeholderCompanyDetails,
} from "../../redux/actions";
import DashboardNotification from "./DashboardNotification";
import { GridLayout } from "../DashboardConfiguration/layoutArrangement";
import { getGraphs } from "../DashboardConfiguration";
import { getUserGraphConfig } from "../../redux/actions/dashboard";
import ProductionScopeGraph from "./ProductionScopeGraph";
import CarbonOffsetCard from "./CarbonOffsetCard";
import OffsetOverYears from "./OffsetOverYears";
import OffsetProgramType from "./OffsetProgramType";
import EmissionvsOffset from "./EmissionvsOffset";
import SustainibilityRiskRating from "./SustainibilityRiskRating";
import AverageScoreSupplierIndustry from "./AverageScoreSupplierIndustry";
import DashboardMatrix from "./DashboardMatrix";
import EmissionPerUnitLine from "./EmissionPerUnitLine";
import EnergyMaterialConsumptionScope3 from "./EnergyMaterialConsumptionScope3";

// ---- Single ESG widget list with categories ----
// Use EXACT keys from getGraphs()
// --- ESG names & normalization (single source of truth) ---
const ESG_WIDGET_CATEGORY_RAW = {
  // Environment
  "Total Emissions": "E",
  "Total Carbon Offset": "E",
  "Emission By Scope": "E",
  "Total CO2e Emissions": "E",
  "Initiative Tracking": "E",
  "Emission By Energy Source": "E",
  "Emission By Energy Source Scope3": "E",
  "Fuel Source Emission": "E",
  "Emission Types(tonnes)": "E",
  "Emission Per Unit Scope": "E",
  "Emission Per Unit Produce": "E",
  "Carbon Offset Card": "E",
  "Offset Over Time": "E",
  "Offset Over Years": "E",
  "Offset Program Type": "E",
  "Offset Program Types": "E",
  "Emission Vs Offset": "E",
  "Sustainability Risk Assessment Ratings": "E",
  "Supplier Overview": "E",
  "Double Materiality Matrix": "E",
  "Magic Quadrant for Supplier Assessment": "E",

  // Social + Governance (share one pill)
  Diversity: "SG",
  "Employee Hire & Resign Trend": "SG",
  "Development & Training": "SG",
  "Social Engagement": "SG",
  "Employee Turnover": "SG",
  "Anti-Corruption Training": "SG",
  "Anti-Corruption Disclosure": "SG",
  "Management Diversity": "SG",
  "Board Diversity": "SG",
  "Ethical Behaviour": "SG",
  "Economic & Financial Disclosure": "SG",
};

const norm = (s) =>
  String(s || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const ESG_ALIASES = {
  "emissions by scope": "emission by scope",
  "emission by energy source scope 3": "emission by energy source scope3",
};

const ESG_CATEGORY = {};
for (const [name, cat] of Object.entries(ESG_WIDGET_CATEGORY_RAW)) {
  ESG_CATEGORY[norm(name)] = cat;
}
const ENV_KEYS = new Set(
  Object.entries(ESG_CATEGORY)
    .filter(([, c]) => c === "E")
    .map(([n]) => n)
);
const SG_KEYS = new Set(
  Object.entries(ESG_CATEGORY)
    .filter(([, c]) => c === "SG")
    .map(([n]) => n)
);

function getPillVisibility(selectedWidgets) {
  if (!Array.isArray(selectedWidgets) || selectedWidgets.length === 0) {
    return { canShowEnvtab: true, canShowSGtab: true };
  }

  const picked = new Set(
    selectedWidgets.map((w) => {
      const n = norm(w);
      return ESG_ALIASES[n] ? norm(ESG_ALIASES[n]) : n;
    })
  );

  const canShowEnvtab = [...ENV_KEYS].some((k) => picked.has(k));
  const canShowSGtab = [...SG_KEYS].some((k) => picked.has(k));

  return { canShowEnvtab, canShowSGtab };
}

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const role = getCookie("role");
  const [filter, setFilter] = useState({ duration: "last_five_years" });
  const [value, setValue] = useState(0);
  const company = useSelector((state) => state.dashboard.companyDetails.data);
  const notificationData = useSelector(
    (state) => state.dashboard.getNotifications.data
  );
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userConfig = useSelector(
    (state) => state.dashboard.getUserGraphConfig.data
  );

  const onApplyFilter = (filter) => {
    setFilter(filter);
  };

  const companyId = Cookies.get("companyId");

  useEffect(() => {
    if (role === "read_only_user")
      dispatch(stakeholderCompanyDetails(companyId));
  }, [dispatch, role, companyId]);

  const timeStamp = Date.now();
  const roleForLayout =
    role === "business_user" || role === "approver" ? "facility_manager" : role;
  useEffect(() => {
    if (
      role !== "read_only_user" &&
      role !== "auditor" &&
      role !== "proxy_sustainability_manager"
    ) {
      dispatch(getNotifications(timeStamp));
    }
  }, [dispatch, role]);
  useEffect(() => {
    if (role !== "read_only_user") dispatch(getUserGraphConfig());
  }, [dispatch]);
  const renderChartsBasedOnRole = (widgetList) => {
    switch (roleForLayout) {
      case "facility_manager":
        return (
          <Grid
            container
            rowSpacing={-2}
            columnSpacing={5}
            className={classes.gridContainer}
          >
            {widgetList?.includes("Audit Trend Graphs") && (
              <>
                <Grid xs={4.8} item>
                  <GeographicalChart filter={filter} />
                </Grid>
                <Grid xs={7.2} item>
                  <FuelSourceGraph filter={filter} />
                </Grid>
                <Grid xs={4.8} item>
                  <EmissionChart filter={filter} />
                </Grid>
                <Grid xs={7.2} item>
                  <AuditTrends filter={filter} size={true} />
                </Grid>
              </>
            )}
          </Grid>
        );
      case "sustainability_manager":
        if (value === 0) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              {widgetList?.includes("Total Emissions") && (
                <>
                  <Grid xs={4.8} item>
                    <EmissionsCard filter={filter} />
                  </Grid>
                  <Grid xs={7.2} item>
                    <EmissionPerUnitLine filter={filter} />
                  </Grid>
                  <Grid xs={4.8} item>
                    <ProductionScopeGraph filter={filter} scope={1} />
                  </Grid>
                  <Grid xs={7.2} item>
                    <FuelSourceGraph filter={filter} />
                  </Grid>
                  <Grid xs={12} item>
                    <FuelSourceTargetGraph filter={filter} />
                  </Grid>
                  <Grid xs={12} item>
                    <EmissionChart filter={filter} />
                  </Grid>
                  <Grid xs={12} item>
                    <TotalEmissionChart filter={filter} />
                  </Grid>
                </>
              )}
              {widgetList?.includes("Carbon Offset Card") && (
                <>
                  <Grid xs={4.8} item>
                    <CarbonOffsetCard filter={filter} />
                  </Grid>
                  <Grid xs={7.2} item>
                    <OffsetOverYears filter={filter} />
                  </Grid>
                  <Grid xs={4.8} item>
                    <OffsetProgramType filter={filter} />
                  </Grid>
                  <Grid xs={7.2} item>
                    <EmissionvsOffset filter={filter} />
                  </Grid>
                </>
              )}
              {[
                "Supplier Overview",
                "Sustainability Risk Assessment Ratings",
                "Initiative Tracking",
                "Emission By Energy Source",
              ].some((item) => widgetList?.includes(item)) && (
                <>
                  <Grid xs={12} marginTop={-2} mb={-2} item>
                    <Typography variant="h6">Drill Down Charts</Typography>
                  </Grid>
                  {widgetList?.includes(
                    "Sustainability Risk Assessment Ratings"
                  ) && (
                    <Grid xs={4.8} item>
                      <SustainibilityRiskRating filter={filter} />
                    </Grid>
                  )}
                  {widgetList?.includes("Supplier Overview") && (
                    <>
                      <Grid xs={7.2} item>
                        <AverageScoreSupplierIndustry filter={filter} />
                      </Grid>
                    </>
                  )}
                  {widgetList?.includes("Initiative Tracking") && (
                    <Grid xs={4.8} item>
                      <InitiativeTrackingGraph filter={filter} />
                    </Grid>
                  )}
                  {widgetList?.includes("Emission By Energy Source") && (
                    <>
                      <Grid xs={7.2} item>
                        <EnergyMaterialConsumption filter={filter} />
                      </Grid>
                      <Grid xs={12} item>
                        <EnergyMaterialConsumptionScope3 filter={filter} />
                      </Grid>
                    </>
                  )}
                  {widgetList?.includes("Supplier Overview") && (
                    <Grid xs={12} item>
                      <MagicQuadrant filter={filter} />
                    </Grid>
                  )}
                </>
              )}
              {widgetList?.includes("Double Materiality Matrix") && (
                <Grid xs={12} marginTop={-2} mb={-2} item>
                  <DashboardMatrix filter={filter} scope={2} />
                </Grid>
              )}
            </Grid>
          );
        }
        if (value === 1) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              <Grid xs={4.8} item>
                <DiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EmployeeHireResignGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <DevelopmentTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <SocialEngagementGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <EmployeeTurnoverGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <BoardDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionDisclosureGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <ManagementDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <EconomicGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EthicalBehaviourGraph filter={filter} resize={true} />
              </Grid>
            </Grid>
          );
        }
        break;
      case "proxy_sustainability_manager":
        if (value === 0) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              <Grid xs={4.8} item>
                <EmissionsCard filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <FuelSourceGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <FuelSourceTargetGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <EmissionChart filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <TotalEmissionChart filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <CarbonOffsetCard filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <OffsetOverYears filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <OffsetProgramType filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EmissionvsOffset filter={filter} />
              </Grid>
              <Grid xs={12} marginTop={-2} mb={-2} item>
                <Typography variant="h6">Drill Down Charts</Typography>
              </Grid>
              <Grid xs={4.8} item>
                <InitiativeTrackingGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EnergyMaterialConsumption filter={filter} />
              </Grid>
            </Grid>
          );
        }
        if (value === 1) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              <Grid xs={4.8} item>
                <DiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EmployeeHireResignGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <DevelopmentTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <SocialEngagementGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <EmployeeTurnoverGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <BoardDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionDisclosureGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <ManagementDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <EconomicGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EthicalBehaviourGraph filter={filter} resize={true} />
              </Grid>
            </Grid>
          );
        }
        break;
      case "read_only_user":
        if (value === 0) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              <Grid xs={12} item>
                <EmissionChart filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <FuelSourceGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <FuelSourceTargetGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <TotalEmissionChart filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <CarbonOffsetCard filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <OffsetOverYears filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <OffsetProgramType filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <Grid xs={7.2} item>
                  <EmissionvsOffset filter={filter} />
                </Grid>
              </Grid>
              <Grid xs={12} marginTop={-2} mb={-2} item>
                <Typography variant="h6">Drill Down Charts</Typography>
              </Grid>
              <Grid xs={4.8} item>
                <InitiativeTrackingGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <EnergyMaterialConsumption filter={filter} />
              </Grid>
            </Grid>
          );
        }
        if (value === 1) {
          return (
            <Grid
              container
              rowSpacing={2}
              columnSpacing={5}
              className={classes.gridContainer}
            >
              {/* <Grid xs={4.8} item>
                <DiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EmployeeHireResignGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <DevelopmentTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <SocialEngagementGraph filter={filter} />
              </Grid>
              <Grid xs={12} item>
                <EmployeeTurnoverGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <BoardDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionDisclosureGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <ManagementDiversityGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <AntiCorruptionTrainingGraph filter={filter} />
              </Grid>
              <Grid xs={4.8} item>
                <EconomicGraph filter={filter} />
              </Grid>
              <Grid xs={7.2} item>
                <EthicalBehaviourGraph filter={filter} resize={true} />
              </Grid> */}
            </Grid>
          );
        }
        break;
      case "admin":
        return (
          <Grid
            container
            rowSpacing={-2}
            columnSpacing={5}
            className={classes.gridContainer}
          >
            <Grid xs={4.8} item>
              <RoleDistributionGraph filter={filter} size={true} />
            </Grid>
            <Grid xs={7.2} item>
              <FuelSourceGraph filter={filter} />
            </Grid>
            <Grid xs={4.8} item>
              <GeographicalChart filter={filter} />
            </Grid>
            <Grid xs={7.2} item>
              <AuditTrends filter={filter} size={true} />
            </Grid>
          </Grid>
        );
      default:
        return (
          <Grid
            container
            rowSpacing={-2}
            columnSpacing={5}
            className={classes.gridContainer}
          >
            {widgetList?.includes("ESG Overview") && (
              <>
                <Grid xs={7.2} item>
                  <EmissionChart filter={filter} />
                </Grid>
                <Grid xs={7.2} item>
                  <FuelSourceGraph filter={filter} />
                </Grid>
                <Grid xs={4.8} item>
                  <GeographicalChart filter={filter} />
                </Grid>
                <Grid xs={7.2} item>
                  <AuditTrends filter={filter} size={true} />
                </Grid>
              </>
            )}
          </Grid>
        );
    }
  };

  const userCfg = userConfig?.configuration;
  const isNewUser =
    !userCfg || !Array.isArray(userCfg.widgets) || userCfg.widgets.length === 0;

  const selectedWidgetsForPills = isNewUser ? [] : userCfg.widgets ?? [];
  const { canShowEnvtab, canShowSGtab } = isNewUser
    ? { canShowEnvtab: true, canShowSGtab: true }
    : getPillVisibility(selectedWidgetsForPills);

  useEffect(() => {
    if (!canShowEnvtab && canShowSGtab && value !== 1) setValue(1);
    else if (canShowEnvtab && !canShowSGtab && value !== 0) setValue(0);
    else if (!canShowEnvtab && !canShowSGtab && value !== 0) setValue(0);
  }, [canShowEnvtab, canShowSGtab]);

  const GRAPHS = getGraphs(onApplyFilter, filter, value, setValue, true, {
    canShowEnvtab,
    canShowSGtab,
  });

  const dashConfig = userCfg || userInfo?.configuration;

  if (dashConfig) {
    if (dashConfig?.layout)
      var layoutType = parseInt(dashConfig?.layout.split("type_")[1]);
    if (dashConfig?.background_color)
      var backgroundColor = dashConfig?.background_color;
    if (dashConfig?.widgets) {
      var selectNames = Object.keys(GRAPHS)
        .filter(
          (item) =>
            dashConfig?.widgets.includes(item) &&
            (dashConfig.widgets.some((widget) => widget === "Filters")
              ? value === GRAPHS[item].tabIndex || GRAPHS[item].tabIndex == null
              : true)
        )
        .map((item) => GRAPHS[item]);
    }
  }

  return (
    <DashboardLayout>
      <Container
        className={classes.container}
        style={{ background: backgroundColor || "#F7F8F9" }}
        maxWidth={false}
        disableGutters
      >
        {role !== "read_only_user" &&
          role !== "auditor" &&
          role !== "proxy_sustainability_manager" && (
            <DashboardNotification notificationData={notificationData} />
          )}
        {role === "read_only_user" && (
          <Card className={classes.companyCard}>
            <Typography variant="h4" className={classes.companyName}>
              {company.name}
            </Typography>
          </Card>
        )}
        {dashConfig?.layout ? (
          <GridLayout
            type={layoutType}
            components={selectNames}
            tab={-1}
            canShowEnvtab={canShowEnvtab}
            canShowSGtab={canShowSGtab}
          />
        ) : (
          <>
            <DashboardHeader
              onApplyFilter={onApplyFilter}
              value={value}
              setValue={setValue}
              isShowConfigButton={true}
              canShowEnvtab={canShowEnvtab}
              canShowSGtab={canShowSGtab}
            />

            <Statistics filter={filter} />
            {renderChartsBasedOnRole(dashConfig?.widgets)}
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default Dashboard;
